import { useState, useEffect } from 'react';
import { useBranch } from '../contexts/BranchContext';
import Modal from '../components/Modal';
import Toast from '../components/Toast';

// สถานะเวอร์ชันโค้ดของแต่ละสาขา — แยกจากคำนวณ "ควรแสดงอะไร" ออกมาให้ทดสอบได้ตรงๆ
// ไม่ต้องผ่าน render ทั้งคอมโพเนนต์ (เหตุผลเดียวกับที่ isCashBreakdownComplete แยกออกมาต่างหาก)
function versionBadge(row) {
  if (!row) return { text: 'กำลังตรวจสอบ...', color: 'var(--muted)' };
  if (row.reachable === false) return { text: '❌ ติดต่อไม่ได้', color: 'var(--danger)' };
  if (row.outdated === null) return { text: 'เทียบไม่ได้', color: 'var(--muted)' };
  if (row.outdated === false) return { text: '✅ ล่าสุด', color: 'var(--ok)' };
  // outdated === true
  if (!row.commit) return { text: '⚠️ ไม่รายงานเวอร์ชัน (โค้ดเก่า)', color: 'var(--warn)' };
  const days = row.behindDays;
  const dayText = days === null ? 'เทียบวันที่ไม่ได้' : `ตามหลัง ${days} วัน`;
  const isVeryOld = typeof days === 'number' && days > 7;
  return {
    text: `⚠️ มีโค้ดใหม่กว่า — ${dayText} (${row.commit.slice(0, 7)})`,
    color: isVeryOld ? 'var(--danger)' : 'var(--warn)'
  };
}

const BLANK_FORM = {
  name: '',
  nameEn: '',
  slug: '',
  code: '',
  address: '',
  taxId: '',
  phone: '',
  promptPayId: '',
  isActive: true
};

export default function BranchManagement({ apiUrl }) {
  const { branches, loading, isOwner } = useBranch();
  const [message, setMessage] = useState(null); // { type: 'success'|'error', text } — แทน alert() เดิม
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState(BLANK_FORM);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [versionsById, setVersionsById] = useState(null); // { [branchId]: row } — null = ยังไม่โหลด

  // ยิงครั้งเดียวตอนเข้าหน้า ไม่ poll — ข้อมูลนี้ไม่ได้เปลี่ยนไวขนาดต้องรีเฟรชสด และ backend
  // ต้องยิงพร้อมกันไปทุกสาขาผ่าน Cloudflare Tunnel ทุกครั้งที่เรียก (มีต้นทุน ไม่ใช่แค่อ่าน DB)
  useEffect(() => {
    if (!isOwner) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${apiUrl}/printer/branch-versions`, {
          headers: { Authorization: `Bearer ${token()}` }
        });
        const body = await res.json().catch(() => null);
        if (!cancelled && res.ok && body?.data) {
          const map = Object.fromEntries(body.data.branches.map(b => [b.id, b]));
          setVersionsById(map);
        }
      } catch {
        // เงียบไว้ — คอลัมน์เวอร์ชันจะค้างที่ "กำลังตรวจสอบ..." ไม่กระทบตารางสาขาหลัก
      }
    })();
    return () => { cancelled = true; };
  }, [apiUrl, isOwner]);

  const token = () => localStorage.getItem('adminToken');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setSaving(true);

    const url = editingId ? `${apiUrl}/api/branches/${editingId}` : `${apiUrl}/api/branches`;
    const method = editingId ? 'PATCH' : 'POST';

    try {
      const res = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token()}` },
        body: JSON.stringify(formData)
      });
      const result = await res.json();
      if (!res.ok) {
        throw new Error(result.error?.message || result.message || 'เกิดข้อผิดพลาด');
      }

      // รีโหลดทั้งหน้า — เพื่อให้ BranchSwitcher/ที่อื่นที่ cache รายชื่อสาขาไว้เห็นข้อมูลล่าสุด
      // (ตามแพทเทิร์นเดิมของระบบ ตอนสลับสาขาก็ reload ทั้งหน้าเหมือนกัน)
      window.location.reload();
    } catch (err) {
      setError(err.message);
      setSaving(false);
    }
  };

  const handleEdit = (branch) => {
    setEditingId(branch.id);
    setFormData({
      name: branch.name || '',
      nameEn: branch.nameEn || '',
      slug: branch.slug || '',
      code: branch.code || '',
      address: branch.address || '',
      taxId: branch.taxId || '',
      phone: branch.phone || '',
      promptPayId: branch.promptPayId || '',
      isActive: branch.isActive !== false
    });
    setShowForm(true);
  };

  const handleAddNew = () => {
    setEditingId(null);
    setFormData(BLANK_FORM);
    setError(null);
    setShowForm(true);
  };

  const handleCancel = () => {
    setEditingId(null);
    setFormData(BLANK_FORM);
    setError(null);
    setShowForm(false);
  };

  const handleToggleActive = async (branch) => {
    try {
      const res = await fetch(`${apiUrl}/api/branches/${branch.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token()}` },
        body: JSON.stringify({ isActive: !branch.isActive })
      });
      if (!res.ok) {
        const result = await res.json();
        throw new Error(result.error?.message || 'เปลี่ยนสถานะไม่สำเร็จ');
      }
      window.location.reload();
    } catch (err) {
      setMessage({ type: 'error', text: err.message });
    }
  };

  if (loading) {
    return <div className="loading">กำลังโหลดข้อมูล...</div>;
  }

  return (
    <div className="branch-management">
      <h2>🏬 จัดการสาขา</h2>

      <Toast message={message} onDismiss={() => setMessage(null)} />

      <button type="button" className="btn-primary" onClick={handleAddNew} style={{ marginBottom: '1.5rem' }}>
        ➕ เพิ่มสาขาใหม่
      </button>

      <Modal open={showForm} onClose={handleCancel} title={editingId ? 'แก้ไขสาขา' : 'เพิ่มสาขาใหม่'}>
        {error && <div className="error-message">{error}</div>}
        <form onSubmit={handleSubmit} className="branch-form">
          <div className="form-row">
            <div className="form-group">
              <label>ชื่อสาขา (ไทย) *</label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
                placeholder="เช่น กบินทร์ 304"
              />
            </div>
            <div className="form-group">
              <label>ชื่อสาขา (อังกฤษ)</label>
              <input
                type="text"
                value={formData.nameEn}
                onChange={(e) => setFormData({ ...formData, nameEn: e.target.value })}
                placeholder="ไม่บังคับ"
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Slug * <span className="hint">(a-z, 0-9, - ใช้ระบุสาขาในระบบ ไม่ควรแก้หลังใช้งานจริงแล้ว)</span></label>
              <input
                type="text"
                value={formData.slug}
                onChange={(e) => setFormData({ ...formData, slug: e.target.value.toLowerCase() })}
                required
                placeholder="เช่น kabin-304"
                style={{ fontFamily: 'monospace' }}
              />
            </div>
            <div className="form-group">
              <label>Code * <span className="hint">(A-Z, 0-9, - ใช้ขึ้นต้นเลขที่ใบเสร็จ)</span></label>
              <input
                type="text"
                value={formData.code}
                onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
                required
                placeholder="เช่น KABIN304"
                style={{ fontFamily: 'monospace' }}
              />
            </div>
          </div>

          <div className="form-group">
            <label>ที่อยู่</label>
            <textarea
              value={formData.address}
              onChange={(e) => setFormData({ ...formData, address: e.target.value })}
              placeholder="ที่อยู่สาขา (ใช้พิมพ์ในใบเสร็จ ไม่บังคับ)"
              rows="2"
            />
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>เลขผู้เสียภาษี</label>
              <input
                type="text"
                value={formData.taxId}
                onChange={(e) => setFormData({ ...formData, taxId: e.target.value })}
                placeholder="ไม่บังคับ"
              />
            </div>
            <div className="form-group">
              <label>เบอร์โทร</label>
              <input
                type="text"
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                placeholder="ไม่บังคับ"
              />
            </div>
          </div>

          <div className="form-group">
            <label>เลขพร้อมเพย์ (สำหรับพิมพ์ QR รับเงินในใบเสร็จ)</label>
            <input
              type="text"
              value={formData.promptPayId}
              onChange={(e) => setFormData({ ...formData, promptPayId: e.target.value })}
              placeholder="เบอร์มือถือ เช่น 0812345678 หรือเลขบัตรประชาชน/ผู้เสียภาษี 13 หลัก — ไม่บังคับ"
            />
          </div>

          {editingId && (
            <div className="form-group">
              <label>
                <input
                  type="checkbox"
                  checked={formData.isActive}
                  onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                />
                {' '}เปิดใช้งานสาขานี้
              </label>
            </div>
          )}

          <div className="form-actions">
            <button type="submit" className="btn-primary" disabled={saving}>
              {saving ? '⏳ กำลังบันทึก...' : (editingId ? 'บันทึกการแก้ไข' : 'เพิ่มสาขา')}
            </button>
            <button type="button" onClick={handleCancel} className="btn-secondary">
              ยกเลิก
            </button>
          </div>
        </form>
      </Modal>

      <div className="branches-list">
        <h3>รายการสาขาทั้งหมด ({branches.length})</h3>
        <div style={{ overflowX: 'auto' }}>
        <table className="data-table table-nowrap">
          <thead>
            <tr>
              <th>ชื่อสาขา</th>
              <th>Slug</th>
              <th>Code</th>
              <th>เบอร์โทร</th>
              <th>สถานะ</th>
              {isOwner && <th>เวอร์ชันโค้ด</th>}
              <th>การจัดการ</th>
            </tr>
          </thead>
          <tbody>
            {branches.length === 0 ? (
              <tr>
                <td colSpan={isOwner ? 7 : 6} style={{ textAlign: 'center' }}>ยังไม่มีสาขาในระบบ</td>
              </tr>
            ) : (
              branches.map((branch) => {
                const badge = isOwner ? versionBadge(versionsById?.[branch.id]) : null;
                return (
                <tr key={branch.id}>
                  <td><strong>{branch.name}</strong></td>
                  <td><code>{branch.slug}</code></td>
                  <td><code>{branch.code}</code></td>
                  <td>{branch.phone || '-'}</td>
                  <td>
                    <span className={`status-badge ${branch.isActive !== false ? 'active' : 'inactive'}`}>
                      {branch.isActive !== false ? 'เปิดใช้งาน' : 'ปิดใช้งาน'}
                    </span>
                  </td>
                  {isOwner && (
                    <td style={{ color: badge.color, fontSize: '0.85rem', whiteSpace: 'nowrap' }}>
                      {badge.text}
                    </td>
                  )}
                  <td>
                    <div className="action-buttons">
                      <button onClick={() => handleEdit(branch)} className="btn-edit" title="แก้ไข">
                        แก้ไข
                      </button>
                      <button onClick={() => handleToggleActive(branch)} className="btn-toggle" title="เปิด/ปิดใช้งาน">
                        {branch.isActive !== false ? 'ปิด' : 'เปิด'}
                      </button>
                    </div>
                  </td>
                </tr>
                );
              })
            )}
          </tbody>
        </table>
        </div>
      </div>

      <style jsx>{`
        .branch-management {
          padding: 20px;
        }

        h2 {
          margin-bottom: 30px;
          color: #333;
        }

        h3 {
          margin-bottom: 15px;
          color: #555;
        }

        .error-message {
          background: #fee;
          color: #c33;
          padding: 12px;
          border-radius: 4px;
          margin-bottom: 20px;
        }

        .branch-form-container {
          background: #f9f9f9;
          padding: 20px;
          border-radius: 8px;
          margin-bottom: 40px;
        }

        .branch-form {
          max-width: 700px;
        }

        .form-group {
          margin-bottom: 20px;
          flex: 1;
        }

        .form-group label {
          display: block;
          margin-bottom: 8px;
          font-weight: 500;
          color: #555;
        }

        .form-group .hint {
          font-weight: 400;
          font-size: 12px;
          color: #888;
        }

        .form-group input[type="text"],
        .form-group textarea {
          width: 100%;
          padding: 10px 12px;
          border: 1px solid #ddd;
          border-radius: 4px;
          font-size: 14px;
          font-family: inherit;
          box-sizing: border-box;
        }

        .form-group input[type="checkbox"] {
          margin-right: 8px;
        }

        .form-row {
          display: flex;
          gap: 20px;
        }

        .form-actions {
          display: flex;
          gap: 10px;
          margin-top: 20px;
        }

        .btn-primary,
        .btn-secondary,
        .btn-edit,
        .btn-toggle {
          padding: 10px 20px;
          border: none;
          border-radius: 4px;
          cursor: pointer;
          font-size: 14px;
          font-weight: 500;
        }

        .btn-primary {
          background: #667eea;
          color: white;
        }

        .btn-primary:hover {
          background: #5568d3;
        }

        .btn-primary:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }

        .btn-secondary {
          background: #e2e8f0;
          color: #4a5568;
        }

        .btn-secondary:hover {
          background: #cbd5e0;
        }

        .branches-list {
          margin-top: 40px;
        }

        .data-table {
          width: 100%;
          border-collapse: collapse;
          background: white;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
        }

        .data-table th,
        .data-table td {
          padding: 12px 15px;
          text-align: left;
          border-bottom: 1px solid #eee;
        }

        .data-table th {
          background: #f7fafc;
          color: #4a5568;
          font-weight: 600;
          font-size: 13px;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }

        .data-table tr:hover {
          background: #f9fafb;
        }

        .status-badge {
          display: inline-block;
          padding: 4px 12px;
          border-radius: 12px;
          font-size: 12px;
          font-weight: 500;
        }

        .status-badge.active {
          background: #d4edda;
          color: #155724;
        }

        .status-badge.inactive {
          background: #f8d7da;
          color: #721c24;
        }

        .action-buttons {
          display: flex;
          gap: 8px;
        }

        .btn-edit {
          background: #48bb78;
          color: white;
          padding: 6px 12px;
          font-size: 13px;
        }

        .btn-edit:hover {
          background: #38a169;
        }

        .btn-toggle {
          background: #ed8936;
          color: white;
          padding: 6px 12px;
          font-size: 13px;
        }

        .btn-toggle:hover {
          background: #dd6b20;
        }

        .loading {
          text-align: center;
          padding: 40px;
          color: #666;
        }
      `}</style>
    </div>
  );
}
